import axios from 'axios';
import { getUrl } from '@/config/api.routes';
const basePath = 'aimanager.forms';
const Q2_PROVIDER = 'q2';

export default {
  namespaced: true,
  state: {
    agentForms: [],
    agentFormItem: null,
  },
  getters: {
    getAgentForms(state) {
      return state.agentForms;
    },
    getAgentFormItem(state) {
      return state.agentFormItem;
    },
  },
  mutations: {
    SET_AGENT_FORMS(state, agentForms) {
      state.agentForms = agentForms;
    },
    SET_AGENT_FORM_ITEM(state, agentFormItem) {
      state.agentFormItem = agentFormItem;
    },
  },
  actions: {

    // GET /forms
    // List all forms with their latest extract/match status.
    async listForms({ commit}) {
      const response = await axios.get(getUrl(`${basePath}`));
      commit('SET_AGENT_FORMS', response.data.models || []);
      return response;
    },

    // GET /forms/:id
    // Fetch a single form. Pass return_fields=true to embed its fields.
    async getForm({ commit }, payload) {
      const { id, return_fields = true } = payload;
      const response = await axios.get(`${getUrl(`${basePath}`)}/${id}`, {
        params: { return_fields },
      });
      commit('SET_AGENT_FORM_ITEM', response.data);
      return response;
    },

    // GET /forms/provider_schema/:provider
    // Return the source-entity bundle for a provider (e.g. q2) used for field matching.
    async getFormProviderSchema(_, payload) {
      const { provider = Q2_PROVIDER } = payload;
      const response = await axios.get(getUrl(`${basePath}/provider_schema/${provider}`));
      return response;
    },

    // POST /forms (multipart, uploaded file)
    // Create a form from an uploaded PDF and start extraction via agentic-forms.
    // Select a PDF for the 'form' field before sending.
    async createFormFromFile(_, payload) {
      const response = await axios.post(getUrl(`${basePath}`), payload);
      return response;
    },

    // PUT /forms/:id
    // Update a form's title and/or draft flag. Publish a form by setting draft=false.
    async updateForm(_, payload) {
      const { id, body } = payload; // { title, draft }

      const response = await axios.put(`${getUrl(`${basePath}`)}/${id}`, body);
      return response;
    },

    // POST /forms/:id/match
    // Kick off field-to-source matching for the form using the given provider's bundle.
    // Requires extraction to have populated schema_json.
    async startFormMatch(_, payload) {
      const { id, provider = Q2_PROVIDER } = payload;
      const response = await axios.post(`${getUrl(`${basePath}`)}/${id}/match`, { provider });
      return response;
    },

    // POST /forms/:id/workflow
    // Compile the form into dialogs + a workflow. endpoint_id is the user-info
    // prefetch endpoint (e.g. q2 user_info). Requires extract and match to be
    // completed and draft=false.
    async compileFormWorkflow(_, payload) {
      const { id, endpoint_id } = payload;
      const response = await axios.post(`${getUrl(`${basePath}`)}/${id}/workflow`, { endpoint_id });
      return response;
    },

    // POST /forms/:form_key/fill
    // Fill the form's PDF from collected entities and upload the result to base-api.
    // Returns { bucket, key } (and base64 when return_base64=true). Normally called
    // by base-api's conversations.form.fill command.
    async fillForm(_, payload) {
      const { formKey, entities, return_base64 } = payload;
      const response = await axios.post(`${getUrl(`${basePath}`)}/${formKey}/fill`, {
        entities,
        return_base64: return_base64 ?? false,
      });
      return response;
    },

    // DELETE /forms/:id
    // Delete a form and its generated dialogs, workflows and per-form entities.
    async deleteForm(_, payload) {
      const { id } = payload;
      const response = await axios.delete(`${getUrl(`${basePath}`)}/${id}`);
      return response;
    },

    // POST /forms/:form_key/workflow_callback (internal)
    // Called by agentic-forms when an extract or match workflow finishes. Included
    // for reference/testing; normally invoked by the engine, not manually. Uses a
    // callback token issued by ai-manager rather than the standard bearer token.
    async formWorkflowCallback(_, payload) {
      const { formKey, callbackToken, ...body } = payload; // { workflow_id, status, fields }
      const response = await axios.post(
        `${getUrl(`${basePath}`)}/${formKey}/workflow_callback`,
        body,
        { headers: { Authorization: `Bearer ${callbackToken}` } },
      );
      return response;
    },



    // GET /forms/:form_id/fields
    // List a form's fields with mapping and match metadata.
    async listFormFields(_, payload) {
      const { formId } = payload;
      const response = await axios.get(`${getUrl(`${basePath}`)}/${formId}/fields`);
      return response;
    },

    // POST /forms/:form_id/fields
    // Add a field to a form. field_key is generated server-side from field_name.
    // field_type is one of: text, checkbox, radio, date, currency, phone, email,
    // account_number, routing_number, signature, initials, table_cell, computed,
    // internal_only.
    async addFormField(_, payload) {
      const { formId, ...body } = payload; // { field_name, field_type, required, mapped_keys, needs_review, match_status }
      const response = await axios.post(`${getUrl(`${basePath}`)}/${formId}/fields`, body);
      return response;
    },

    // PUT /forms/:form_id/fields/:field_id
    // Update a form field's name/type/required/mapped_keys/match_status.
    async updateFormField(_, payload) {
      const { formId, fieldId, ...body } = payload;
      const response = await axios.put(
        `${getUrl(`${basePath}`)}/${formId}/fields/${fieldId}`,
        body,
      );
      return response;
    },

    // DELETE /forms/:form_id/fields/:field_id
    // Delete a single form field.
    async deleteFormField(_, payload) {
      const { formId, fieldId } = payload;
      const response = await axios.delete(`${getUrl(`${basePath}`)}/${formId}/fields/${fieldId}`);
      return response;
    },
  },
};